import { useMemo } from "react";
import { useAccount } from "wagmi";
import { useTranslation } from "next-i18next";
import { useURLParams } from "@looksrare/utils";
import { RaffleSortInput } from "views/win/userGeneratedRaffles/types";
import { RAFFLE_CARDS_PER_PAGE } from "views/win/userGeneratedRaffles/utils/hooks";

enum FilterKey {
  ALL = "ALL",
  MY_RAFFLES = "MY_RAFFLES",
  MY_PARTICIPATIONS = "MY_PARTICIPATIONS",
}
export interface FilterOption {
  label: string;
  filterKey: FilterKey;
}

export const useFilterOptions = (): FilterOption[] => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      { label: t("All Raffles"), filterKey: FilterKey.ALL },
      { label: t("My Created Raffles"), filterKey: FilterKey.MY_RAFFLES },
      { label: t("My Participated Raffles"), filterKey: FilterKey.MY_PARTICIPATIONS },
    ],
    [t]
  );
};

export const useCommunityRafflesFilter = () => {
  const { address } = useAccount();
  const { params, setParams, removeParam } = useURLParams();
  const filterOptions = useFilterOptions();

  const selectedOption = useMemo(() => {
    const urlFilterKey = params.filter;

    const matchingFilterOption = filterOptions.find((option) => option.filterKey === urlFilterKey?.toUpperCase());

    if (!matchingFilterOption) {
      removeParam("filter");
      return filterOptions[0];
    }
    return matchingFilterOption;
  }, [params.filter, removeParam, filterOptions]);

  const setSelectedOption = (option: FilterOption) => {
    setParams(({ ...prevParams }) => {
      delete prevParams.page;
      prevParams.filter = option.filterKey;
      return prevParams;
    });
  };

  const filter = (() => {
    if (selectedOption.filterKey === FilterKey.MY_RAFFLES) {
      return { owner: address };
    }
    if (selectedOption.filterKey === FilterKey.MY_PARTICIPATIONS) {
      return { participant: address };
    }
    // return undefined for FilterKey.ALL
    return undefined;
  })();

  return { filter, selectedOption, setSelectedOption };
};

/**
 * Manage CommunityRaffles sort options
 */
export const useRaffleSort = () => {
  const { t } = useTranslation();
  return useMemo(() => {
    const raffleSort = {
      percentRemaining: {
        label: t("% Tickets Remaining"),
        sortKey: RaffleSortInput.PCT_ENTRIES_LEFT_DESC,
      },
      newest: {
        label: t("Newest"),
        sortKey: RaffleSortInput.CREATED_DESC,
      },
      oldest: {
        label: t("Oldest"),
        sortKey: RaffleSortInput.CREATED_ASC,
      },
      timeRemaining: {
        label: t("Time Remaining"),
        sortKey: RaffleSortInput.ENDING_SOON,
      },
    };
    return {
      raffleSort,
      defaultSort: raffleSort.percentRemaining,
      getSortFromLabel: (label: string) => {
        return Object.values(raffleSort).find((sort) => sort.label === label);
      },
    };
  }, [t]);
};

export const useCommunityRafflesSort = () => {
  const { params, setParams, removeParam } = useURLParams();
  const { raffleSort } = useRaffleSort();

  const currentPageNumber = useMemo(() => {
    const parsedPageNumber = parseInt(params.page || "1");

    if (isNaN(parsedPageNumber)) {
      removeParam("page");
      return 1;
    }
    return parsedPageNumber;
  }, [params.page, removeParam]);

  const setCurrentPageNumber = (pageNumber: number) => {
    setParams((prevParams) => ({ ...prevParams, page: pageNumber.toString() }));
  };

  const sort = useMemo(() => {
    const matchingSort = Object.values(raffleSort).find(
      (sortOption) => sortOption.sortKey === params.sort?.toUpperCase()
    );
    if (!matchingSort) {
      removeParam("sort");
      return raffleSort.percentRemaining;
    }

    return matchingSort;
  }, [params.sort, removeParam, raffleSort]);

  const setSort = (sortOption: (typeof raffleSort)[keyof typeof raffleSort]) => {
    setParams(({ ...prevParams }) => {
      delete prevParams.page;
      prevParams.sort = sortOption.sortKey;
      return prevParams;
    });
  };

  const offset = (currentPageNumber - 1) * RAFFLE_CARDS_PER_PAGE;

  return {
    offset,
    sort,
    setSort,
    currentPageNumber,
    setCurrentPageNumber,
  };
};
